import MobxReactForm from 'mobx-react-form';
import dvr from 'mobx-react-form/lib/validators/DVR';
import validatorjs from 'validatorjs';
import ProfileStore from '../../../../stores/ProfileStore';
import AppStore from '../../../../stores/AppStore';
import { GENDERS, TITLES } from '../../../../framework/constants/profile';
import { PHONE_PREFIX_DEFAULT, PHONE_PREFIXES } from '../../../../framework/constants/forms';
import { toFieldsNames, toMobxFields } from './MobxFormGenerator';
import { isNil } from 'lodash';
const fieldHooks = {
    patch: (field) => {
        ProfileStore.patchDealerProfileEdit({ [field.name]: field.isEmpty ? null : field.values() });
    },
    printError: (field) => {
        console.log('error', field);
    }
};
const flatFieldProps = {
    title: {
        type: 'text',
        extra: TITLES
    },
    street: {
        type: 'text',
        rules: ['required']
    },
    number: {
        type: 'text',
        rules: ['required']
    },
    streetAddition: {
        type: 'text'
    },
    city: {
        type: 'text',
        rules: ['required']
    },
    zip: {
        type: 'text',
        rules: ['required']
    },
    region: {
        type: 'text'
    },
    firstName: {
        type: 'text',
        rules: ['required']
    },
    lastName: {
        type: 'text',
        rules: ['required']
    },
    gender: {
        type: 'text',
        rules: ['required'],
        extra: GENDERS,
        output: (valueObj) => {
            return valueObj ? valueObj.value : null;
        }
    },
    company: {
        type: 'text',
        rules: ['required']
    },
    phoneMain: {
        name: 'phoneMain',
        label: 'ContactPage:phone',
        rules: ['required'],
        // default: '',
        output: (value) => {
            const { countryCode, areaCode, phoneNumber } = value;
            // console.log(value);
            const phone = (countryCode && areaCode && phoneNumber) || (phoneNumber) ? `${countryCode ? countryCode : ''}${areaCode ? areaCode : ''}${phoneNumber}` : '';
            return phone ? phone : null;
        }
    },
    phoneMobile: {
        output: (value) => {
            const { countryCode, areaCode, phoneNumber } = value;
            const phone = areaCode || phoneNumber ? `${countryCode ? countryCode : ''}${areaCode ? areaCode : ''}${phoneNumber}` : '';
            return phone ? phone : null;
        }
    },
    fax: {
        output: (value) => {
            const { countryCode, areaCode, phoneNumber } = value;
            const phone = countryCode && areaCode && phoneNumber ? `${countryCode ? countryCode : ''}${areaCode}${phoneNumber}` : '';
            return phone ? phone : null;
        }
    },
    country: {
        type: 'text',
        rules: ['required'],
        extra: []
    },
    ustIdNr: {
        type: 'text',
        rules: ['required']
    },
    email: {
        type: 'email',
        rules: ['required', 'email']
    },
    website: {
        type: 'url',
        rules: ['internet_address']
    },
    accountHolder: {
        type: 'text'
    },
    iban: {
        type: 'text',
        rules: ['iban'],
        output: value => value.replace(/\s/g, '')
    },
    bic: {
        type: 'text',
        rules: ['bic']
    },
    en: {
        type: 'text',
        placeholder: '',
        rules: ['required']
    },
    de: {
        type: 'text',
        placeholder: '',
        rules: ['required']
    },
    emailPermission: {
        type: 'checkbox',
        rules: ['required', 'boolean']
    },
    commercialRegister: {
        type: 'text',
        rules: ['required']
    },
    commercialRegisterNumber: {
        type: 'text',
        rules: ['required']
    },
    authorizedRepresentative: {
        type: 'text',
        rules: ['required']
    },
    countryCode: {
        extra: PHONE_PREFIXES,
        default: PHONE_PREFIX_DEFAULT.value,
        value: PHONE_PREFIX_DEFAULT,
        output: (data) => {
            if (isNil(data)) {
                return '';
            }
            else {
                return data.value ? data.value : data;
            }
        }
    },
    areaCode: {
        type: 'tel',
        rules: ['phone_areaCode'],
        default: ''
    },
    phoneNumber: {
        type: 'tel',
        rules: ['phone_phoneNumber'],
        default: ''
    },
    countryCodeRequired: {
        extra: PHONE_PREFIXES,
        value: PHONE_PREFIX_DEFAULT,
        rules: ['required'],
        output: (data) => {
            if (typeof data !== 'undefined') {
                return data.value ? data.value : data;
            }
            else {
                return PHONE_PREFIX_DEFAULT;
            }
        }
    },
    areaCodeRequired: {
        type: 'tel',
        rules: ['phone_areaCode', 'required']
    },
    phoneNumberRequired: {
        type: 'tel',
        rules: ['phone_phoneNumber', 'required']
    }
};
const toFieldsProps = ({ patch, printError }, { title, street, number, streetAddition, city, zip, region, firstName, lastName, gender, company, phoneMain, phoneMobile, fax, country, ustIdNr, email, website, accountHolder, iban, bic, en, de, emailPermission, commercialRegister, commercialRegisterNumber, authorizedRepresentative, countryCode, areaCode, phoneNumber, countryCodeRequired, areaCodeRequired, phoneNumberRequired }) => ({
    emailPermission,
    email: {
        ...email,
        hooks: {
            onSuccess: patch,
            onError: printError
        }
    },
    website: {
        ...website,
        hooks: {
            onSuccess: patch,
            onError: printError
        }
    },
    mainAddress: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            title,
            street,
            number,
            streetAddition,
            city,
            zip,
            region,
            firstName,
            lastName,
            gender,
            company,
            ustIdNr,
            country,
            commercialRegister,
            commercialRegisterNumber,
            authorizedRepresentative,
            phoneMain: {
                ...phoneMain,
                fields: {
                    countryCode: countryCodeRequired,
                    areaCode: areaCodeRequired,
                    phoneNumber: phoneNumberRequired
                }
            },
            phoneMobile: {
                ...phoneMobile,
                fields: {
                    countryCode,
                    areaCode,
                    phoneNumber
                }
            },
            fax: {
                ...fax,
                fields: {
                    countryCode,
                    areaCode,
                    phoneNumber
                }
            }
        }
    },
    contactAddress: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            title,
            firstName,
            lastName,
            gender,
            email,
            phoneMain: {
                ...phoneMain,
                fields: {
                    countryCode: countryCodeRequired,
                    areaCode: areaCodeRequired,
                    phoneNumber: phoneNumberRequired
                }
            },
            phoneMobile: {
                ...phoneMobile,
                fields: {
                    countryCode,
                    areaCode,
                    phoneNumber
                }
            }
        }
    },
    billingAddress: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            title,
            street,
            number,
            streetAddition,
            city,
            zip,
            region,
            firstName,
            lastName,
            gender,
            company,
            email,
            country
        }
    },
    businessAddress: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            title,
            street,
            number,
            streetAddition,
            city,
            zip,
            region,
            firstName,
            lastName,
            gender,
            company,
            email,
            country
        }
    },
    paymentAccount: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            accountHolder,
            iban,
            bic
        }
    },
    imprint: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            de
        }
    },
    termCond: {
        hooks: {
            onSuccess: patch,
            onError: printError
        },
        fields: {
            de
        }
    }
});
const fieldsProps = toFieldsProps(fieldHooks, flatFieldProps);
const fieldsNames = toFieldsNames(fieldsProps);
const options = {
    showErrorsOnReset: false,
    validateOnChange: true
};
const plugins = {
    dvr: dvr({
        package: validatorjs
    })
};
const hooks = {
    onInit: (form) => {
        AppStore.getCountries().then((countries) => {
            const extra = countries.map((country) => ({ value: country.id, label: country.name })).filter((country) => ((country.label !== 'EU version' && country.label !== 'EU-Ausführung')));
            form.each((field) => {
                if (field.name === fieldsNames.country) {
                    field.set('extra', extra);
                }
            });
        });
    }
};
const form = new MobxReactForm({ fields: toMobxFields(fieldsProps) }, { options, plugins, hooks });
export { fieldsNames };
export default form;
